// Operational Sessions page (formerly the dashboard) — accessed via /sessions
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import { api } from "../lib/api";
import {
  ArrowRight, Users, Building2, ShieldCheck, HeartPulse, AlertOctagon, Gavel,
  Flag, Trophy, Radio, Users2
} from "lucide-react";

const SUB_DEFS = [
  { key: "people", label: "People", Icon: Users },
  { key: "premises", label: "Premises", Icon: Building2 },
  { key: "safeguarding", label: "Safeguarding", Icon: ShieldCheck },
  { key: "first_aid", label: "First aid", Icon: HeartPulse },
  { key: "open_incidents", label: "Incidents", Icon: AlertOctagon },
  { key: "approval", label: "Approval", Icon: Gavel },
];

const KIND_META = {
  junior: { label: "Junior session", Icon: Trophy },
  members: { label: "Members evening", Icon: Users },
  briefing: { label: "Paddock briefing", Icon: Radio },
  marshals: { label: "Trackside rota", Icon: Flag },
  mixed: { label: "Mixed session", Icon: Users2 },
};

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) +
    " · " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function Sessions() {
  const [events, setEvents] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.listEvents(), api.listPeople()]).then(([ev, pp]) => {
      setEvents(ev); setPeople(pp); setLoading(false);
    });
  }, []);

  const officers = people.filter((p) =>
    ["Club Secretary", "Safeguarding Lead", "Maintenance & Scrutineering Lead", "Duty Manager"].includes(p.role_title)
  );

  return (
    <div className="space-y-10" data-testid="sessions-page">
      <section className="space-y-3">
        <div className="tracking-eyebrow text-muted-foreground">Operational readiness</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-semibold">Sessions &amp; events</h1>
        <p className="text-sm text-muted-foreground max-w-2xl">
          From paddock access to safeguarding checks, ClubCierge routes the right issue to the right officer
          before the session proceeds. Drivers can't be shared with sponsors while operational items are open.
        </p>
        <div className="pt-2">
          <Link to="/intake"><Button variant="outline" data-testid="sessions-report-cta"><Flag className="h-4 w-4 mr-1.5" /> Report an issue</Button></Link>
        </div>
      </section>

      {loading && <Card className="p-8 text-sm text-muted-foreground">Loading sessions…</Card>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {events.map((ev) => <EventCard key={ev.id} ev={ev} />)}
      </div>

      <section className="space-y-3 pt-4">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Officers on duty</div>
          <h2 className="text-2xl font-semibold mt-1">Who decides what</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {officers.map((p) => (
            <Card key={p.id} className="p-4 border-border race-card flex items-start gap-3" data-testid={`officer-${p.id}`}>
              <img src={p.avatar_url} alt="" className="h-10 w-10 rounded-full object-cover bg-muted" />
              <div className="min-w-0">
                <div className="font-medium text-[14px] truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.role_title}</div>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

function EventCard({ ev }) {
  const r = ev.readiness;
  const meta = KIND_META[ev.kind] || KIND_META.mixed;
  return (
    <Card className="p-5 border-border race-card flex flex-col gap-4 hover:shadow-md transition-shadow" data-testid={`event-card-${ev.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-1.5 tracking-eyebrow text-muted-foreground">
            <meta.Icon className="h-3 w-3" /> {meta.label}
          </div>
          <h3 className="text-[17px] font-semibold leading-snug">{ev.title}</h3>
          <div className="text-[11px] text-muted-foreground font-mono-ui">{formatDate(ev.start_at)} · {ev.location}</div>
        </div>
        <StatusBadge status={r.overall} size="lg" testId={`event-rag-${ev.id}`} />
      </div>

      <div className="grid grid-cols-3 gap-1.5">
        {SUB_DEFS.map(({ key, label, Icon }) => {
          const s = r.sub_scores[key];
          const color = s === "green" ? "var(--rag-green-text)" : s === "amber" ? "var(--rag-amber-text)" : "var(--rag-red-text)";
          return (
            <div key={key} className="rounded-md border border-border p-2" data-testid={`sub-${ev.id}-${key}`}>
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <Icon className="h-3 w-3" />{label}
              </div>
              <div className="mt-0.5 text-[11px] font-medium font-mono-ui" style={{ color }}>
                {s.toUpperCase()}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 text-[11px] text-muted-foreground border-t border-border pt-3">
        <div>{r.open_exception_count} open · {r.open_incident_count} incident{r.open_incident_count === 1 ? "" : "s"}</div>
        <div className="font-mono-ui tracking-wide">
          {r.ready_to_proceed ? "READY" : ev.approval_status === "approved" ? "APPROVED" : "AWAITING SIGN-OFF"}
        </div>
      </div>

      <Link to={`/events/${ev.id}`} className="mt-auto">
        <Button variant="outline" size="sm" className="w-full justify-between" data-testid={`open-event-${ev.id}`}>
          Open readiness <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </Link>
    </Card>
  );
}
